import React from 'react';
import { Product } from '@/app/data';
import { Trash2, ArrowRight } from 'lucide-react';

interface CartProps {
  items: Product[];
  onRemove: (id: string) => void;
  onCheckout: () => void;
  onContinueShopping: () => void;
}

export const Cart: React.FC<CartProps> = ({ items, onRemove, onCheckout, onContinueShopping }) => {
  const total = items.reduce((sum, item) => sum + item.price, 0);

  if (items.length === 0) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center p-4 text-center">
        <h2 className="text-2xl font-black italic text-[#061970] mb-4">TU CARRITO ESTÁ VACÍO</h2>
        <p className="text-gray-500 mb-8">Parece que aún no has elegido tu protección ideal.</p>
        <button 
            onClick={onContinueShopping}
            className="px-8 py-3 bg-[#459EDF] text-white font-bold uppercase italic rounded-full shadow-lg hover:shadow-[#459EDF]/40 transition-all"
        >
            Ver Colección 2026
        </button>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      <h1 className="text-3xl font-black italic text-[#061970] mb-8 uppercase tracking-tighter border-b-4 border-[#FF7F32] inline-block pb-2 pr-12">
        Tu Equipo
      </h1>

      <div className="grid md:grid-cols-3 gap-8">
        {/* Items List */}
        <div className="md:col-span-2 space-y-4">
          {items.map((item, index) => (
            <div key={`${item.id}-${index}`} className="flex gap-4 p-4 bg-white rounded-xl shadow-sm border border-gray-100 items-center">
              <div className="w-24 h-24 bg-gray-50 rounded-lg overflow-hidden flex-shrink-0">
                <img src={item.image} alt={item.name} className="w-full h-full object-cover mix-blend-multiply" />
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-[#061970] uppercase italic leading-tight">{item.name}</h3>
                <span className="text-xs text-gray-500 uppercase tracking-wider">{item.category.join(' • ')}</span>
                <div className="mt-2 text-[#459EDF] font-bold text-lg">${item.price.toLocaleString()}</div>
              </div>
              <button 
                onClick={() => onRemove(item.id)}
                className="p-2 text-gray-300 hover:text-red-500 transition-colors"
              >
                <Trash2 className="w-5 h-5" />
              </button>
            </div>
          ))}
        </div>

        {/* Summary */}
        <div className="md:col-span-1">
            <div className="bg-white p-6 rounded-2xl shadow-lg border border-[#061970]/10 sticky top-24">
                <h3 className="font-bold text-[#061970] uppercase mb-4 text-lg">Resumen</h3>
                
                <div className="space-y-2 mb-6 text-sm text-gray-600">
                    <div className="flex justify-between">
                        <span>Subtotal</span>
                        <span>${total.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between">
                        <span>Envío</span>
                        <span className="text-[#FF7F32] font-bold">GRATIS</span>
                    </div>
                    <div className="flex justify-between font-bold text-[#061970] text-xl pt-4 border-t border-gray-100 mt-2">
                        <span>Total</span>
                        <span>${total.toLocaleString()}</span>
                    </div>
                </div>

                <button 
                    onClick={onCheckout}
                    className="w-full py-4 bg-[#061970] text-white font-black italic uppercase rounded-lg shadow-xl flex items-center justify-center gap-2 group hover:bg-[#FF7F32] transition-colors"
                >
                    Pagar Ahora <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </button>
                
                <div className="mt-4 flex gap-2 justify-center opacity-50 grayscale">
                    {/* Mock Payment Icons */}
                    <div className="w-8 h-5 bg-gray-300 rounded" />
                    <div className="w-8 h-5 bg-gray-300 rounded" />
                    <div className="w-8 h-5 bg-gray-300 rounded" />
                </div>
            </div>
        </div>
      </div>
    </div>
  );
};
